import { useEffect, useState } from "react";
import { Play, X } from "lucide-react";
import { SectionLabel } from "@/components/yanre/SectionLabel";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { useLocale } from "@/i18n/LocaleContext";

// Reusable Customer Cases — data-driven; videos lazy-load only inside the modal.
export const CustomerCases = ({ cases = [], index = "13" }) => {
  const { t } = useLocale();
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="projects" className="section-darker relative overflow-hidden border-t border-carbon-700" data-testid="customer-cases-section">
      <div className="container-edge relative py-24 lg:py-32">
        <Reveal>
          <SectionLabel index={index}>YANRE Gyms Around the World</SectionLabel>
        </Reveal>
        <h2 className="mt-8 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight text-white sm:text-6xl lg:text-7xl">
          <RevealLines
            lines={[
              t("cc.h1a", "Real gyms."),
              t("cc.h1b", "Real businesses."),
              <span key="r">
                {t("cc.h1c", "Real YANRE.")}
              </span>,
            ]}
          />
        </h2>
        <div className="mt-16 grid gap-px md:grid-cols-3" data-testid="cases-grid">
          {cases.map((c, i) => (
            <Reveal key={c.country} delay={i * 0.08}>
              <button
                type="button"
                onClick={() => setActive(c)}
                className="group relative block h-full w-full overflow-hidden border border-carbon-700 text-left rtl:text-right"
                data-testid={`case-${c.country.toLowerCase()}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={c.poster}
                    alt={`${c.country} — ${t(`cc.case${i}.title`, c.title)}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-carbon-950/95 via-carbon-950/30 to-transparent" />
                <div className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center border border-white/30 bg-carbon-950/50 backdrop-blur-sm transition-colors duration-300 group-hover:border-signal group-hover:bg-signal">
                  <Play className="h-5 w-5 fill-white text-white" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">
                    {c.country}{c.city ? ` — ${c.city}` : ""}
                  </p>
                  <h3 className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight text-white">
                    {t(`cc.case${i}.title`, c.title)}
                  </h3>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-carbon-950/90 p-4 backdrop-blur-md"
          onClick={() => setActive(null)}
          data-testid="case-modal"
        >
          <div
            className="relative w-full max-w-4xl border border-carbon-700 bg-carbon-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-carbon-700 px-6 py-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-titanium/70">
                {active.country}{active.city ? ` — ${active.city}` : ""} · {t(`cc.case${cases.indexOf(active)}.title`, active.title)}
              </p>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="flex h-9 w-9 items-center justify-center border border-carbon-600 text-titanium transition-colors hover:border-signal hover:text-white"
                aria-label="Close"
                data-testid="case-modal-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="aspect-video bg-carbon-950">
              {active.videoUrl ? (
                <video className="h-full w-full" controls autoPlay preload="none" poster={active.poster} data-testid="case-video">
                  <source src={active.videoUrl} type="video/mp4" />
                </video>
              ) : (
                <div className="relative flex h-full items-center justify-center">
                  <img src={active.poster} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
                  <p className="relative px-6 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-titanium/70">
                    {t("cc.videoPending", "Full project video available on request")}
                  </p>
                </div>
              )}
            </div>
            {active.description && (
              <p className="border-t border-carbon-700 px-6 py-5 text-sm leading-relaxed text-titanium/70">
                {active.description}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
